import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useParams } from 'react-router-dom';

const EmpInfo = () => {
  const { id } = useParams();

  const [emp, setEmp] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tickets, setTickets] = useState([]);

  const [loading, setLoading] = useState(true);

  // Filters state
  const [taskFilters, setTaskFilters] = useState({ status: '', startDate: '', endDate: '' });
  const [ticketFilters, setTicketFilters] = useState({ status: '', startDate: '', endDate: '' });

  // Filtered data to show
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);

  // Pagination
  const itemsPerPage = 10;
  const [currentTaskPage, setCurrentTaskPage] = useState(1);
  const [currentTicketPage, setCurrentTicketPage] = useState(1);

  // Fetch employee, tasks, tickets
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [empRes, tasksRes, ticketsRes] = await Promise.all([
          fetch(`https://apiaksharaconnect.aksharafinalytics.in/empLists/${id}`),
          fetch(`https://apiaksharaconnect.aksharafinalytics.in/emptasks/${id}`),
          fetch(`https://apiaksharaconnect.aksharafinalytics.in/assignedtickets/${id}`)
        ]);

        if (!empRes.ok || !tasksRes.ok || !ticketsRes.ok) {
          throw new Error('Failed to fetch data');
        }

        const empData = await empRes.json();
        const tasksData = await tasksRes.json();
        const ticketsData = await ticketsRes.json();

        setEmp(empData);
        setTasks(tasksData.task || []);
        setTickets(ticketsData.ticket || []);

        setFilteredTasks(tasksData.task || []);
        setFilteredTickets(ticketsData.ticket || []);
      } catch (error) {
        console.error(error);
        setEmp(null);
        setTasks([]);
        setTickets([]);
        setFilteredTasks([]);
        setFilteredTickets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const formatName = (name) => {
    if (!name) return 'Employee Name';
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleInputChange = (type, field, value) => {
    if (type === 'task') {
      setTaskFilters((prev) => ({ ...prev, [field]: value }));
    } else {
      setTicketFilters((prev) => ({ ...prev, [field]: value }));
    }
  };

  const applyFilters = () => {
    // Filter tasks
    let tempTasks = [...tasks];
    if (taskFilters.status) {
      tempTasks = tempTasks.filter(
        (t) => t.status?.toLowerCase() === taskFilters.status.toLowerCase()
      );
    }
    if (taskFilters.startDate) {
      tempTasks = tempTasks.filter((t) => {
        if (!t.doa) return false;
        return new Date(t.doa) >= new Date(taskFilters.startDate);
      });
    }
    if (taskFilters.endDate) {
      tempTasks = tempTasks.filter((t) => {
        if (!t.doa) return false;
        return new Date(t.doa) <= new Date(taskFilters.endDate);
      });
    }
    setFilteredTasks(tempTasks);
    setCurrentTaskPage(1);

    // Filter tickets
    let tempTickets = [...tickets];
    if (ticketFilters.status) {
      tempTickets = tempTickets.filter(
        (t) => t.status?.toLowerCase() === ticketFilters.status.toLowerCase()
      );
    }
    if (ticketFilters.startDate) {
      tempTickets = tempTickets.filter((t) => {
        if (!t.assigned_date) return false;
        return new Date(t.assigned_date) >= new Date(ticketFilters.startDate);
      });
    }
    if (ticketFilters.endDate) {
      tempTickets = tempTickets.filter((t) => {
        if (!t.assigned_date) return false;
        return new Date(t.assigned_date) <= new Date(ticketFilters.endDate);
      });
    }
    setFilteredTickets(tempTickets);
    setCurrentTicketPage(1);
  };

  const resetTaskFilters = () => {
    setTaskFilters({ status: '', startDate: '', endDate: '' });
    setFilteredTasks(tasks);
    setCurrentTaskPage(1);
  };

  const resetTicketFilters = () => {
    setTicketFilters({ status: '', startDate: '', endDate: '' });
    setFilteredTickets(tickets);
    setCurrentTicketPage(1);
  };

  const paginate = (data, page) => {
    const start = (page - 1) * itemsPerPage;
    return data.slice(start, start + itemsPerPage);
  };

  const renderPagination = (totalItems, currentPage, setPage) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (totalPages <= 1) return null;

    let pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setPage(i)}
          className={`btn btn-sm mx-1 ${currentPage === i ? 'btn-primary' : 'btn-outline-primary'}`}
          type="button"
        >
          {i}
        </button>
      );
    }
    return <div className="mt-3">{pages}</div>;
  };

    const exportToExcel = (data, fileName) => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        XLSX.writeFile(workbook, `${fileName}.xlsx`);
    };

// Optional: cleaner format for export
    const formatTasksForExport = (data) =>
        data.map(({ task_uniqueid, Username, Sname, Plan, status, doa }) => ({
            'Task ID': task_uniqueid,
            User: Username,
            Service: Sname,
            Plan,
            Status: status,
            'Assigned Date': doa ? new Date(doa).toLocaleDateString() : '',
     }));

    const formatTicketsForExport = (data) =>
        data.map(({ ticket_id, Username, status, assigned_date }) => ({
            'Ticket ID': ticket_id,
            User: Username,
            Status: status,
            'Assigned Date': assigned_date ? new Date(assigned_date).toLocaleDateString() : '',
    }));

    const isTaskFilterApplied = () => {
        return taskFilters.status || taskFilters.startDate || taskFilters.endDate;
    };

    const isTicketFilterApplied = () => {
        return ticketFilters.status || ticketFilters.startDate || ticketFilters.endDate;
    };


  if (loading) {
    return (
      <div className="text-center mt-4">
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
        Loading...
      </div>
    );
  }

  if (!emp) {
    return (
      <div className="text-center mt-4">
        <p>No employee data found.</p>
      </div>
    );
  }

  return (
    <>
      <div className="container mt-5">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5><b>{formatName(emp.name)}</b></h5>
            </div>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <ul className="list-group list-group-flush">
                  <li className="list-group-item"><strong>Email:</strong> {emp.email || 'N/A'}</li>
                  <li className="list-group-item"><strong>Phone:</strong> {emp.phone || 'N/A'}</li>
                </ul>
              </div>
              <div className="col-sm-12 col-md-6">
                <ul className="list-group list-group-flush">
                  <li className="list-group-item"><strong>Role:</strong> {emp.role || 'N/A'}</li>
                  <li className="list-group-item"><strong>Date:</strong> {emp.date || 'N/A'}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* TASKS FILTER */}
        <div className="card mt-5">
          <div className="card-body">
            <h4><b>Tasks</b></h4><hr/>
            <div className="row align-items-end mb-3">
              <div className="col-md-2">
                <label>Status</label>
                <select className="form-select" value={taskFilters.status} onChange={e => handleInputChange('task', 'status', e.target.value)}>
                  <option value="">All</option>
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div className="col-md-3">
                <label>Start Date</label>
                <input type="date" className="form-control" value={taskFilters.startDate} onChange={e => handleInputChange('task', 'startDate', e.target.value)} />
              </div>
              <div className="col-md-3">
                <label>End Date</label>
                <input type="date" className="form-control" value={taskFilters.endDate} onChange={e => handleInputChange('task', 'endDate', e.target.value)} />
              </div>
              <div className="col-md-4 d-flex gap-2">
                <button className="btn btn-primary flex-grow-1" onClick={applyFilters}>Search</button>
                <button className="btn btn-secondary flex-grow-1" onClick={resetTaskFilters}>Reset</button>
              </div>
            </div>
            <div className="mb-2 d-flex gap-2">
                <button className="btn btn-outline-success btn-sm" onClick={() => exportToExcel(formatTasksForExport(tasks), 'All_Tasks')}>
                    Export All Tasks
                </button>
                 {isTaskFilterApplied() && (
                    <button className="btn btn-outline-primary btn-sm" onClick={() => exportToExcel(formatTasksForExport(filteredTasks), 'Filtered_Tasks')} >
                    Export Filtered Tasks
                    </button>
                )}
            </div>

            <hr/>
            <div className="table-responsive">
              <table className="table table-bordered table-hover table-striped">
                <thead className="table-dark">
                  <tr>
                    <th>S.no</th>
                    <th>Task ID</th>
                    <th>User</th>
                    <th>Service</th>
                    <th>Plan</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredTasks, currentTaskPage).map((task, index) => (
                    <tr key={task.id}>
                      <td>{(currentTaskPage - 1) * itemsPerPage + index + 1}</td>
                      <td className='text-danger'>{task.task_uniqueid}</td>
                      <td className='text-primary'>{task.Username || 'N/A'}</td>
                      <td>{task.Sname || 'N/A'}</td>
                      <td>{task.Plan || 'N/A'}</td>
                      <td className={task.status === 'closed' ? 'text-danger' : 'text-primary'}>{task.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {renderPagination(filteredTasks.length, currentTaskPage, setCurrentTaskPage)}
          </div>
        </div>

        {/* TICKETS FILTER */}
        <div className="card mt-5">
          <div className="card-body">
            <h4><b>Tickets</b></h4><hr/>
            <div className="row align-items-end mb-3">
              <div className="col-md-2">
                <label>Status</label>
                <select className="form-select" value={ticketFilters.status} onChange={e => handleInputChange('ticket', 'status', e.target.value)}>
                  <option value="">All</option>
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div className="col-md-3">
                <label>Start Date</label>
                <input type="date" className="form-control" value={ticketFilters.startDate} onChange={e => handleInputChange('ticket', 'startDate', e.target.value)} />
              </div>
              <div className="col-md-3">
                <label>End Date</label>
                <input type="date" className="form-control" value={ticketFilters.endDate} onChange={e => handleInputChange('ticket', 'endDate', e.target.value)} />
              </div>
              <div className="col-md-4 d-flex gap-2">
                <button className="btn btn-primary flex-grow-1" onClick={applyFilters}>Search</button>
                <button className="btn btn-secondary flex-grow-1" onClick={resetTicketFilters}>Reset</button>
              </div>
            </div>
            <div className="mb-2 d-flex gap-2">
                <button className="btn btn-outline-success btn-sm" onClick={() => exportToExcel(formatTicketsForExport(tickets), 'All_Tickets')}>
                    Export All Tickets
                </button>
                {isTicketFilterApplied() && (
                    <button className="btn btn-outline-primary btn-sm" onClick={() => exportToExcel(formatTicketsForExport(filteredTickets), 'Filtered_Tickets')}>
                        Export Filtered Tickets
                    </button>
                 )}
            </div>

            <hr/>
            <div className="table-responsive">
              <table className="table table-bordered table-hover table-striped">
                <thead className="table-dark">
                  <tr>
                    <th>S.no</th>
                    <th>Ticket ID</th>
                    <th>User</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredTickets, currentTicketPage).map((ticket, index) => (
                    <tr key={ticket.id}>
                      <td>{(currentTicketPage - 1) * itemsPerPage + index + 1}</td>
                      <td className='text-danger' >{ticket.ticket_id}</td>
                      <td className='text-primary'>{ticket.Username || 'N/A'}</td>
                      <td className={ticket.status === 'closed' ? 'text-danger' : 'text-primary'}>{ticket.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {renderPagination(filteredTickets.length, currentTicketPage, setCurrentTicketPage)}
          </div>
        </div>
      </div>
    </>
  );
};

export default EmpInfo;
